import { createAsyncThunk } from '@reduxjs/toolkit'

import type { PickerSubmissionPayload } from 'slices/aiConversations/pickerTypes'
import type { SegmentSaveData } from 'slices/aiConversations/segmentVisualizationTypes'
import { createAnotherSegment } from 'slices/aiSegmentBuilder'
import { resetAllPickers } from 'slices/aiSegmentBuilder/picker'
import { webSocketRegistry } from 'utils/webSocketContextAdapter'

import {
  selectConnectionStatus,
  selectWebSocketError,
  selectWebSocketManagerId,
  setAwaitingResponse,
  setChatId,
  setIsLoading,
  setPickerSubmitted,
  updateMessages,
} from 'slices/aiConversations'

export const saveSegmentThunk = createAsyncThunk(
  'aiConversations/saveSegment',
  async (segmentData: SegmentSaveData, { getState, rejectWithValue }) => {
    const state = getState() as { aiConversations: any }

    // Check Redux connection state first
    const connectionStatus = selectConnectionStatus(state)
    const connectionError = selectWebSocketError(state)
    const managerId = selectWebSocketManagerId(state)

    if (connectionError) {
      return rejectWithValue(`WebSocket connection error: ${connectionError}`)
    }

    if (connectionStatus !== 'connected') {
      return rejectWithValue(
        `WebSocket not connected (status: ${connectionStatus})`
      )
    }

    if (!managerId) {
      return rejectWithValue('No WebSocket manager registered')
    }

    const manager = webSocketRegistry.get(managerId)
    if (!manager) {
      return rejectWithValue('WebSocket manager not found in registry')
    }

    if (!manager.connected) {
      return rejectWithValue('WebSocket manager not connected')
    }

    try {
      await Promise.resolve(manager.saveSegment(segmentData))
      return segmentData
    } catch (error) {
      return rejectWithValue('Failed to save segment')
    }
  }
)

/** The chat message that carries a picker response to thanx-ai */
export const pickerResponseMessage = (payload: PickerSubmissionPayload) =>
  `[Picker Response] ${JSON.stringify(payload)}`

interface SubmitPickerResponseArgs {
  messageId: string
  payload: PickerSubmissionPayload
}

// Thunk for submitting picker response via WebSocket (hidden from UI)
export const submitPickerResponseThunk = createAsyncThunk(
  'aiConversations/submitPickerResponse',
  async (
    { messageId, payload }: SubmitPickerResponseArgs,
    { dispatch, getState, rejectWithValue }
  ) => {
    const state = getState() as { aiConversations: any }

    // Check Redux connection state first
    const connectionStatus = selectConnectionStatus(state)
    const connectionError = selectWebSocketError(state)
    const managerId = selectWebSocketManagerId(state)

    if (connectionError) {
      return rejectWithValue(`WebSocket connection error: ${connectionError}`)
    }

    if (connectionStatus !== 'connected') {
      return rejectWithValue(
        `WebSocket not connected (status: ${connectionStatus})`
      )
    }

    if (!managerId) {
      return rejectWithValue('No WebSocket manager registered')
    }

    const manager = webSocketRegistry.get(managerId)
    if (!manager) {
      return rejectWithValue('WebSocket manager not found in registry')
    }

    if (!manager.connected) {
      return rejectWithValue('WebSocket manager not connected')
    }

    try {
      // Send the picker response as a message (not visible in UI)
      manager.sendMessage(pickerResponseMessage(payload))

      // Mark the picker as submitted to make it read-only
      dispatch(setPickerSubmitted({ messageId, submitted: true }))

      // Set loading state to show typing indicator while waiting for response
      dispatch(setAwaitingResponse(true))
      dispatch(setIsLoading(true))

      return { messageId, payload }
    } catch (error) {
      return rejectWithValue('Failed to submit picker response')
    }
  }
)

// Unified thunk for creating another segment
export const createAnotherSegmentThunk = createAsyncThunk(
  'aiConversations/createAnotherSegment',
  async (_, { dispatch, getState }) => {
    const state = getState() as { aiConversations: any }
    const managerId = selectWebSocketManagerId(state)

    // Reset chat ID if there's an active manager
    if (managerId) {
      const manager = webSocketRegistry.get(managerId)
      if (manager && manager.setChatId) {
        manager.setChatId(null)
      }
    }

    // A stale Redux chat id is re-injected as `initialChatId` on the next
    // (re)connect, resuming the previous conversation.
    dispatch(setChatId(null))
    dispatch(updateMessages([]))
    dispatch(resetAllPickers())
    dispatch(createAnotherSegment())
  }
)
